import React, { createContext, useEffect, useRef, useState } from 'react';
import { MarketSignalStream } from '@/modules/market-signals/model/market-signals/market-signal-stream';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import { MarketSignal } from '@/modules/market-signals/model/market-signals/market-signal';
import { MarketSignalStreamAnalysis } from '@/modules/market-signals/model/market-signals/market-signal-stream-analysis';
import { MarketSignalAlarm } from '@/modules/market-signals/model/market-signals/market-signal-alarm';
import { useSubscription } from '@/core/messaging/export';
import { copyText } from '@/modules/market-signals/components/market-signals/market-signal-copy-resource';

export interface MarketSignalContextType {
  selectedStream?: string;
  setSelectedStream: (s?: string) => void;
  streams: MarketSignalStream[];
  setStreams: (s: MarketSignalStream[]) => void;
  selectedItem?: MarketSignal;
  setSelectedItem: (s?: MarketSignal) => void;
  hoveredItem?: MarketSignal;
  setHoveredItem: (s?: MarketSignal) => void;
  selectedAlarm?: MarketSignalAlarm;
  setSelectedAlarm: (s?: MarketSignalAlarm) => void;
  analysis?: MarketSignalStreamAnalysis;
  setAnalysis: (s?: MarketSignalStreamAnalysis) => void;
  notificationText: React.MutableRefObject<Map<string, string>>;
  updateNotificationText: (s: MarketSignal[]) => void;

  alarms?: MarketSignalAlarm[];
  setAlarms: (s?: MarketSignalAlarm[]) => void;
  acknowledgeAlarms: (ids: string[]) => void;
  alarmsForSignal: (id: string) => MarketSignalAlarm[];
}

export const MarketSignalContext = createContext<MarketSignalContextType>({
  streams: [],
  setStreams: s => {},
  selectedStream: undefined,
  setSelectedStream: s => {},
  setSelectedItem: s => {},
  setAnalysis: s => {},
  setSelectedAlarm: s => {},
  setHoveredItem: s => {},
  notificationText: { current: new Map() },
  updateNotificationText: s => {},

  acknowledgeAlarms: ids => {},
  setAlarms: s => {},
  alarmsForSignal: id => [],
});

interface MarketSignalSound {
  name: string;
  file: string;
}

export const marketSignalSounds: MarketSignalSound[] = [
  { name: '<No Sound>', file: '' },
  { name: 'Angry Beep', file: 'angry_beep' },
  { name: 'Catastrophe', file: 'siren' },
  { name: 'Critical Notice', file: 'radio' },
  { name: 'Downblip', file: 'blip' },
  { name: 'Night Chime', file: 'chime' },
  { name: 'Pop!', file: 'pop' },
  { name: 'Wonder Bell', file: 'bell' },
];

export function MarketSignalContextProvider({ children }: React.PropsWithChildren<{}>) {
  const [streams, setStreams] = useState<MarketSignalStream[]>([]);
  const [analysis, setAnalysis] = useState<MarketSignalStreamAnalysis>();
  const [alarms, setAlarms] = useState<MarketSignalAlarm[]>();
  const [selectedItem, setSelectedItem] = useState<MarketSignal>();
  const [hoveredItem, setHoveredItem] = useState<MarketSignal>();
  const [selectedAlarm, setSelectedAlarm] = useState<MarketSignalAlarm>();
  const [selectedStream, setSelectedStream] = useState<string | undefined>(
    () => localStorage.getItem('ms-selected-stream') || undefined,
  );
  const notificationText = useRef<Map<string, string>>(new Map());
  const updateNotificationText = (newSignals: MarketSignal[]) => {
    newSignals.forEach(s => {
      !notificationText.current.has(s._id) && notificationText.current.set(s._id, copyText(s));
    });
    if (notificationText.current.size >= 1000) {
      const keys = notificationText.current.keys();
      let current = keys.next();
      let toDelete = [];
      while (!current.done) {
        toDelete.push(current.value);
        current = keys.next();
      }

      toDelete = toDelete.slice(-900);
      toDelete.forEach(k => notificationText.current.delete(k));
    }
  };

  const acknowledgeAlarms = async (ids: string[]) => {
    try {
      const updates = await Promise.all(ids.map(id => marketSignalApi.acknowledgeAlarm(id)));
      const successes = updates.filter((u): u is MarketSignalAlarm => !!u);

      setAlarms(prev => prev?.map(a => successes.find(s => s._id === a._id) || a));
    } catch (e) {
      console.error('failed to update alarms', e);
    }
  };

  useEffect(() => {
    marketSignalApi.getStreams().then(v => {
      const { content } = v;
      setStreams(content);

      const localStream = localStorage.getItem('ms-selected-stream');
      if (localStream) {
        const isValid = content.some(s => s.code === localStream);
        if (isValid) {
          setSelectedStream(localStream);
          return;
        }
      }

      if (content.length > 0) {
        setSelectedStream(content[0].code);
      }
    });
  }, []);

  useEffect(() => {
    selectedStream && localStorage.setItem('ms-selected-stream', selectedStream);
  }, [selectedStream]);

  const playing = useRef(false);
  const playSound = async (s: string) => {
    if (playing.current || s.trim() === '') return;

    console.log('Playing sound: ', s);
    const audio = new Audio(`/sounds/${s}.mp3`);

    try {
      playing.current = true;
      await audio.play();
    } catch (e) {
      console.error('Error playing sound');
    } finally {
      playing.current = false;
      console.log('Playing sound finished:', s);
    }
  };

  useSubscription<MarketSignalAlarm>('/topic/signals/alert', message => {
    let sendAlert = true;
    setAlarms(prev => {
      const alarms = prev || [];
      if (alarms.some(a => a._id === message._id)) {
        sendAlert = false;
        return alarms;
      }
      return [message, ...alarms].slice(0, 25);
    });
    if (!sendAlert) return;

    playSound(message.soundType || '');
    if (message.showDesktopNotif && 'Notification' in window) {
      const createNotif = () => {
        const bodyText = notificationText.current.get(message.signalId);
        const notification = bodyText
          ? new Notification(`ALERT: ${message.reason}`, { body: bodyText })
          : new Notification(`ALERT: New alert, text too old to retrieve`);

        notification.onclick = async event => {
          event.preventDefault();
          window.focus();
          bodyText && setSelectedAlarm(message);
          await new Promise(p => setTimeout(p, 75));
          try {
            bodyText && navigator.clipboard.writeText(bodyText);
          } catch (e) {
            console.error(e);
          }
          notification.close();
        };
      };

      if (Notification.permission === 'granted') createNotif();
      else if (Notification.permission === 'default')
        Notification.requestPermission().then(p => p === 'granted' && createNotif());
    }
  });

  return (
    <MarketSignalContext.Provider
      value={{
        selectedStream,
        setSelectedStream,
        streams,
        setStreams,
        selectedItem,
        setSelectedItem,
        analysis,
        setAnalysis,
        hoveredItem,
        setHoveredItem,
        notificationText,
        updateNotificationText,

        alarms,
        setAlarms,
        acknowledgeAlarms,
        alarmsForSignal: id => {
          return alarms?.filter(a => a.signalId === id) || [];
        },
        selectedAlarm,
        setSelectedAlarm,
      }}
    >
      {children}
    </MarketSignalContext.Provider>
  );
}
